import React, { useCallback, useEffect, useState } from "react";

import { getBankingDetails, getFarmDetails, updateBankingDetails } from "services";

import { incompleteMessage, useCancelAsyncOnUnmount, useNavigate, useToggleNavs, useTranslation, useUserContext } from "utils";

import { Button, Col, ErrorBoundary, ExtraInfo, Form, Link, MainPage, NavigationHeader, Row, Typography, Message as message } from "components";

import { AddressInformation } from "pages/app/wallet/components/AddressInformation";
import { BankInformation } from "pages/app/wallet/components/BankInformation";

export const BankingDetails = () => {
  const { t } = useTranslation(["points", "meta", "onboarding"]);
  const { user } = useUserContext();

  useToggleNavs(true, false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState({});
  const [toggleModalTaxRate, setToggleModalTaxRate] = useState(false);
  const [toggleModalFarmNumber, setToggleModalFarmNumber] = useState(false);
  const [farm, setFarm] = useState({});
  const [addressStreet, setAddressStreet] = useState("");
  const signal = useCancelAsyncOnUnmount()();
  const navigate = useNavigate();

  const loadFarmDetails = useCallback(() => {
    setLoading(true);

    if (!user?.farm) {
      return;
    }

    getFarmDetails({
      farmId: user.farm.id,
      signal,
    })
      .then((res) => {
        setFarm(res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [signal, user?.farm]);

  const loadBankingDetails = useCallback(() => {
    setLoading(true);

    getBankingDetails({
      signal,
    }).then((res) => {
      setDetails(res);
      setAddressStreet(res.addressStreet);
      loadFarmDetails();
    });
  }, [loadFarmDetails, signal]);

  const complete = (v) => {
    setSaving(true);

    updateBankingDetails({
      ...v,
      addressStreet: addressStreet,
    })
      .then(() => {
        navigate("/farm");
        message({ content: t("bankingSaved"), icon: "tick" });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  useEffect(() => {
    loadBankingDetails();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage loading={loading} pageName={"profile"} containerClass="tiny">
      {!loading && (
        <Form name="profile" onFinish={(v) => complete(v)} onFinishFailed={() => incompleteMessage(t("errors.mandatoryFields"))}>
          <NavigationHeader title={farm.bankingDetails ? t("bankingHeader.update") : t("bankingHeader.add")} />
          <Row>
            <Col>
              <Typography variant="h1" className="margin-bottom-350">
                {t("bankingHeadline")}
              </Typography>
            </Col>
          </Row>
          <ErrorBoundary>
            <BankInformation details={details} setToggleModalTaxRate={setToggleModalTaxRate} />
          </ErrorBoundary>
          <ErrorBoundary>
            <AddressInformation
              addressStreet={addressStreet}
              details={details}
              farmCode={farm.code}
              setAddressStreet={setAddressStreet}
              setToggleModalFarmNumber={setToggleModalFarmNumber}
              t={t}
            />
          </ErrorBoundary>
          <Row className="margin-top-400">
            <Col md={3} sm={0} />
            <Col md={18} sm={24} className="center">
              <Form.Item>
                <Button type="primary" htmlType="submit" disabled={loading || saving} loading={loading || saving}>
                  {t("bankingUpdateButton")}
                </Button>
              </Form.Item>
            </Col>
            <Col md={3} sm={0} />
          </Row>
          <Row>
            <Col className="center">
              <Typography variant="paragraph-m">
                <Link to={"/farm"}>{t("backButton")}</Link>
              </Typography>
            </Col>
          </Row>
        </Form>
      )}
      <ExtraInfo setVisible={setToggleModalTaxRate} type="taxRate" visible={toggleModalTaxRate} />
      <ExtraInfo setVisible={setToggleModalFarmNumber} type="eu-farm-number-explanation" visible={toggleModalFarmNumber} />
    </MainPage>
  );
};
